import React from 'react';
import { createRoot } from 'react-dom/client';
import * as Sentry from '@sentry/browser';
import { HashRouter, BrowserRouter } from 'react-router-dom';
import { Provider } from 'react-redux';
import { Utils, addDefinition } from 'versafleet-core';
import en from 'languages/en.json';

import ReduxActions, { store } from 'reduxActions';
import ReduxConstantActions from 'reduxActions/constant';
import ReduxJobActions from 'reduxActions/job';
import ReduxNotificationActions from 'reduxActions/notification';
import ReduxProfileActions from 'reduxActions/profile';
import ReduxBillingAccountActions from 'reduxActions/billingAccount';
import ReduxTaskActions from 'reduxActions/task';
import ReduxFilterActions from 'reduxActions/filter';
import ReduxAccountActions from 'reduxActions/account';
import ReduxGlobalSearchActions from 'reduxActions/globalSearch';
import ReduxCustomFieldGroupActions from 'reduxActions/customFieldGroup';
import ReduxLanguageActions from 'reduxActions/language';
import ReduxOurLocationActions from 'reduxActions/ourLocation';
import ReduxCustomLayoutActions from 'reduxActions/customLayout';
import ReduxVersadriveSettingActions from 'reduxActions/versadriveSetting';

import routes from './routes';
import mobileRoutes from './mobileRoutes';

import '../stylesheets/main.styl';
import '../../node_modules/versafleet-core/assets/icons.css';

// Merge MyJobs language definitions with versafleet-core's
addDefinition('en', en);

// Set Sentry configuration
if (!__DEV__) {
  Sentry.init({
    dsn: 'https://53d8a9654faf4de58ac30f9ffbd6d0f1@o250887.ingest.us.sentry.io/4351532',
    release: __VERSION__,
    environment: process.env.sentryEnvironment,
    ignoreErrors: ['ChunkLoadError'],
    beforeSend(event, hint) {
      // If the error is a Timeout error, ignore it
      // https://github.com/getsentry/sentry-javascript/issues/2514
      if (hint.originalException === 'Timeout') {
        return null;
      }
      return event;
    },
  });
}

/* eslint-disable */
// Segment
// this below commented for https://versafleet.atlassian.net/browse/CP-283
// localStorage.removeItem('ajs_user_traits');
// const storageSettings = { localStorage: { enabled: false } };
// const analytics = window.analytics = window.analytics || []; if (!analytics.initialize) {
//   if (analytics.invoked)window.console && console.error && console.error('Segment snippet included twice.'); else {
//     analytics.invoked = !0; analytics.methods = ['init', 'trackSubmit', 'trackClick', 'trackLink', 'trackForm', 'pageview', 'identify', 'reset', 'group', 'track', 'ready', 'alias', 'page', 'once', 'off', 'on']; analytics.factory = function (t) { return function () { const e = Array.prototype.slice.call(arguments); e.unshift(t); analytics.push(e); return analytics; }; }; for (let t = 0; t < analytics.methods.length; t++) { const e = analytics.methods[t]; analytics[e] = analytics.factory(e); }analytics.load = function (t) { const e = document.createElement('script'); e.type = 'text/javascript'; e.async = !0; e.src = `${document.location.protocol === 'https:' ? 'https://' : 'http://'}cdn.segment.com/analytics.js/v1/${t}/analytics.min.js`; const n = document.getElementsByTagName('script')[0]; n.parentNode.insertBefore(e, n); }; analytics.SNIPPET_VERSION = '3.1.0';
//     analytics.init(null, storageSettings);
//     analytics.load(process.env.segmentKey);
//     analytics.page();
//   }
// }
/* eslint-enable */

/* Redux */
ReduxActions.setActions({
  constant: ReduxConstantActions,
  job: ReduxJobActions,
  notification: ReduxNotificationActions,
  profile: ReduxProfileActions,
  billingAccount: ReduxBillingAccountActions,
  task: ReduxTaskActions,
  filter: ReduxFilterActions,
  account: ReduxAccountActions,
  globalSearch: ReduxGlobalSearchActions,
  customFieldGroup: ReduxCustomFieldGroupActions,
  language: ReduxLanguageActions,
  ourLocation: ReduxOurLocationActions,
  customLayout: ReduxCustomLayoutActions,
  versadriveSetting: ReduxVersadriveSettingActions,
});

if (Utils.UserAgentDetector.isMobileOrTablet()) {
  document.getElementsByTagName('html')[0].setAttribute('class', 'mobile');
}

const renderedRoutes = Utils.UserAgentDetector.isMobileOrTablet() ? mobileRoutes : routes;

Utils.RequestHelper.setBaseUrl(`${process.env.apiUrl}/myjobs`);

Utils.RequestHelper.setPreemptiveFunctionHandler((response) => {
  if (response.status === 401 && Utils.Router.getPath().indexOf('auth') === -1) {
    return response.clone().json().then((e) => {
      if (e.message.length > 0 && e.message[0] === 'SSO') { // SSO IdP Logout
        Utils.Router.replace(`/auth/login/sso/${e.message[1]}`);
      } else { // Auto logout when timeout
        Utils.Router.replace('/auth/login');
      }
      return response;
    });
  }
  return response;
});

Utils.PusherHelper.setKey(process.env.pusherKey);

const Router = __DEV__ ? HashRouter : BrowserRouter;

const container = document.getElementById('main-container');
const root = createRoot(container);

root.render(
  <Provider store={store}>
    <Router>
      {renderedRoutes}
    </Router>
  </Provider>,
);
